import { collectionOf, moduleApi, resourceOf } from '@pray/shared/utils/api';
import type { ApiCollection } from '@pray/shared/utils/api';

import type {
  SegmentCount,
  SegmentCreate,
  SegmentDetail,
  SegmentField,
  SegmentGroup,
  SegmentListParams,
  SegmentPreview,
  SegmentSummary,
  SegmentUpdate,
} from './types';
import {
  decodeSegmentCount,
  decodeSegmentDetail,
  decodeSegmentField,
  decodeSegmentPreview,
  decodeSegmentSummary,
  encodeSegmentCreate,
  encodeSegmentNode,
  encodeSegmentUpdate,
} from './wire';

/**
 * server_pray's segmentation routes on the Studio surface. Every route is gated by an IAM permission no role holds
 * yet, so only Pray studio admins pass; a refusal arrives as an `ApiProblemError` with status 403.
 */
const SEGMENTS = '/api/studio/v1/segments';

const segmentPath = (id: string) => `${SEGMENTS}/${encodeURIComponent(id)}`;

/** The fields a condition may filter on, with the operators each allows. One page: the catalog is small. */
export async function getSegmentFields(): Promise<SegmentField[]> {
  return collectionOf(await moduleApi.get(`${SEGMENTS}/fields`), decodeSegmentField).items;
}

/** One page of the user segments, the most recently changed first. Hand `pagination.nextCursor` back as `cursor`. */
export async function listSegments(params: SegmentListParams = {}): Promise<ApiCollection<SegmentSummary>> {
  const query: Record<string, unknown> = {};
  if (params.status) query.status = params.status;
  if (params.q) query.q = params.q;
  if (params.cursor) query.cursor = params.cursor;
  if (params.limit) query.limit = params.limit;

  return collectionOf(await moduleApi.get(SEGMENTS, query), decodeSegmentSummary);
}

/** The segment with its tree, read from the primary: the version to send back with an update. */
export async function getSegment(id: string): Promise<SegmentDetail> {
  return resourceOf(await moduleApi.get(segmentPath(id)), decodeSegmentDetail);
}

export async function createSegment(create: SegmentCreate): Promise<SegmentDetail> {
  return resourceOf(await moduleApi.post(SEGMENTS, encodeSegmentCreate(create)), decodeSegmentDetail);
}

/**
 * Saves the fields given over the stored segment. A version that is no longer the stored one is refused with a 409
 * `ApiProblemError`: someone else saved since it was read, and the editor reads the segment again.
 */
export async function updateSegment(id: string, update: SegmentUpdate): Promise<SegmentDetail> {
  return resourceOf(await moduleApi.patch(segmentPath(id), encodeSegmentUpdate(update)), decodeSegmentDetail);
}

/** Archives by status alone: no tree is checked, so a segment naming a retired field can still be archived. */
export async function archiveSegment(id: string, expectedVersion: number | null): Promise<SegmentDetail> {
  return updateSegment(id, { expectedVersion, status: 'archived' });
}

/**
 * A saved segment's size as its last refresh left it, and why that refresh is failing when it is. A draft, or a segment
 * not refreshed yet, has no size. Only a studio admin may ask, at most 30 times a minute; an unreachable segment store
 * answers 503.
 */
export async function countSegment(id: string): Promise<SegmentCount> {
  return resourceOf(await moduleApi.post(`${segmentPath(id)}/count`), decodeSegmentCount);
}

const PREVIEWS = `${SEGMENTS}/preview-count`;

/**
 * Starts counting a tree not yet saved, and answers at once with the preview to poll; a tree already counted over the
 * same data answers `counted` with `reused` instead. `fresh` always runs a new count. Only production counts: elsewhere
 * the route answers 503. A preview already counting for this user is refused with 429, an invalid tree with 422.
 */
export async function startSegmentPreview(
  root: SegmentGroup,
  options: { fresh?: boolean } = {}
): Promise<SegmentPreview> {
  return resourceOf(
    await moduleApi.post(PREVIEWS, { root: encodeSegmentNode(root), ...(options.fresh ? { fresh: true } : {}) }),
    decodeSegmentPreview
  );
}

/** The preview as it stands: `running` until it is counted or has failed. Only the user who started it may read it. */
export async function getSegmentPreview(id: string): Promise<SegmentPreview> {
  return resourceOf(await moduleApi.get(`${PREVIEWS}/${encodeURIComponent(id)}`), decodeSegmentPreview);
}
